import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzMessageService } from 'ng-zorro-antd/message';
import { ActivatedRoute, Router } from '@angular/router';
import { LayoutService } from '../../layout/layout.service';
import { ApiPurpleFitCompleteRegistrationRequest, ApiPurpleFitRegisterRequest, ApiPurpleFitUpdateUserInfoRequest, PRPVUserFull } from '../../../core-modules/sdk';
import { environment } from '../../../environments/default/environment';
import { CommonModule } from '@angular/common';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { PurpleTranslationPipe, PurpleTranslationService } from '@purple-lib/purple-translations-core';
import { PurpleFormComponent, PurpleFormFieldsGroup, PurpleSimpleButtonComponent, PurpleSimpleNotificationService } from '@purple-lib/purple-ui-widgets';
import { PurpleBreakpointsService, PurpleLoaderService, PurpleStorageService } from '@purple-lib/purple-utilities';
import { LoginType, ManagedAuthenticationProvider, PurpleUserService } from '@purple-lib/purple-users-core';
import { Location } from '@angular/common';
import { UserCredential, signInWithPopup, GoogleAuthProvider, OAuthProvider, getAuth } from 'firebase/auth';
import { initializeApp } from '@angular/fire/app';
import { PurpleEditableImageDirective } from '@purple-lib/purple-edit-medias';
import { PurpleEditableTextDirective } from '@purple-lib/purple-edit-translations';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { PurpleFitCoreTranslations } from '../../../core-modules/constants/purple-fit-core-translations';
import { NzModalService } from 'ng-zorro-antd/modal';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['../authentication.scss', './login.component.scss'],
  imports: [CommonModule, FormsModule, ReactiveFormsModule, PurpleTranslationPipe, NzButtonModule,
    PurpleEditableTextDirective, PurpleSimpleButtonComponent, NzCheckboxModule, PurpleFormComponent, PurpleEditableImageDirective]
})
export class LoginComponent implements OnInit {
  protected readonly T = PurpleFitCoreTranslations;

  constructor(private fb: FormBuilder, public breakSvc: PurpleBreakpointsService, private loaderSvc: PurpleLoaderService, private layoutSvc: LayoutService,
    private userSvc: PurpleUserService<PRPVUserFull, ApiPurpleFitRegisterRequest, ApiPurpleFitCompleteRegistrationRequest, ApiPurpleFitUpdateUserInfoRequest>, public tsvc: PurpleTranslationService,
    private modalSvc: NzModalService,
    private mesSvc: NzMessageService, private location: Location, private storageSvc: PurpleStorageService, private router: Router, private route: ActivatedRoute, private psNotSvc: PurpleSimpleNotificationService) {
    this.validateForm = this.fb.group({})
  }

  validateForm!: FormGroup;
  purpleFormFieldGroups: PurpleFormFieldsGroup[] | undefined;
  rememberCheck: boolean = true;
  selectedCompany: string | undefined;
  isLoading: boolean = false;
  private firebaseAuth: any;

  ngOnInit() {
    //this.loaderSvc.addRequest(environment.LOADER_ID);
    this.route.queryParams.subscribe(async params => {
      this.layoutSvc.setRedirectUrl(params, false);
    });

    const app = initializeApp(environment.firebase);
    this.firebaseAuth = getAuth(app);

    this.purpleFormFieldGroups = [
      {
        fieldGroupNumber: 1,
        fieldGroupBootstrapColumn: 12,
        fieldGroupPaddingLeft: false,
        fieldGroupPaddingRight: false,
        formFieldFormGroup: this.validateForm,
        formFields: [
          {
            fieldType: 'input-text',
            fieldControlName: 'email',
            fieldBootstrapColumn: 12,
            fieldName: this.tsvc.getTranslationValue('login_page_form_email', 'Email', []),
            fieldPlaceholder: "",
            fieldValue: undefined,
            fieldIsRequired: true,
            fieldPaddingLeft: false,
            fieldPaddingRight: false,
            fieldCustomValidators: [Validators.email],
            fieldCustomValidatorErrors: [{ errorId: "email", text: this.tsvc.getTranslationValue("invalid_email_validator_error_text", "Email non valida") }]
          },
          {
            fieldType: 'input-text',
            fieldControlName: 'password',
            fieldBootstrapColumn: 12,
            fieldName: this.tsvc.getTranslationValue('login_page_form_password', 'Password', []),
            fieldPlaceholder: "",
            fieldValue: undefined,
            fieldIsRequired: true,
            fieldPaddingLeft: false,
            fieldPaddingRight: false,
            fieldOptions: [{ id: 'isPassword', value: true }]
          }
        ]
      }
    ]
    this.selectedCompany = localStorage.getItem("CompanyName") ?? undefined;
    this.loaderSvc.removeRequest(environment.LOADER_ID);
  }

  async login() {
    this.isLoading = true;
    for (const i in this.validateForm.controls) {
      this.validateForm.controls[i].markAsDirty();
      this.validateForm.controls[i].updateValueAndValidity();
    }

    if (this.validateForm.valid) {

      await this.userSvc.login({
        clientTypeId: "web",
        loginField1: this.validateForm.controls["email"].value,
        password: this.validateForm.controls["password"].value,
        loginType: LoginType.Email,
        clientId: environment.appName,
        clientVersion: environment.appVersion
      }, async (_: PRPVUserFull, isLoggedIn: boolean) => {
        if (isLoggedIn) {
          if (this.layoutSvc.redirectUrl != undefined) {
            await this.router.navigateByUrl(this.layoutSvc.redirectUrl)
          } else {
            await this.router.navigate([this.tsvc.currentLanguage(), environment.CLIENT_ROUTE, 'home'])
          }
        }
      }, false)
    }
    this.isLoading = false;
  }

  loginWithSocial(social: ManagedAuthenticationProvider): void {
    let authProvider: any | undefined;

    switch (social) {
      case 'google':
        authProvider = new GoogleAuthProvider();
        authProvider.setCustomParameters({ locale: this.tsvc.currentLanguage(), prompt: "consent" });
        break;
      case 'apple':
        authProvider = new OAuthProvider('apple.com');
        authProvider.setCustomParameters({ locale: this.tsvc.currentLanguage() });
        break;

      default:
        throw Error("Not implemented")
    }

    signInWithPopup(this.firebaseAuth, authProvider)
      .then(async (result: UserCredential) => {
        this.loaderSvc.addRequest(environment.LOADER_ID);

        const user = result.user;
        const credential = OAuthProvider.credentialFromResult(result);

        if (credential == null) {
          throw Error('Failed to get credential from result');
        }

        var displayName = user.providerData[0].displayName;
        var name = displayName == null ? ["User", "User"] : displayName.split(" ");

        await this.userSvc.loginWithToken({
          idToken: credential.idToken!,
          username: name[0],
          lastname: name[1],
          provider: social,
          clientTypeId: "web",
          clientId: environment.appName,
          clientVersion: environment.appVersion
        }, async (_: PRPVUserFull, isLoggedIn: boolean) => {
          if (isLoggedIn) {
            if (this.layoutSvc.redirectUrl != undefined) {
              await this.router.navigateByUrl(this.layoutSvc.redirectUrl)
            } else {
              await this.router.navigate([this.tsvc.currentLanguage(), environment.CLIENT_ROUTE, 'home'])
            }
          } else {
            throw Error();
          }
        }, true, environment.LOADER_ID, environment.API_CALL_LOADER_DELAY);

        this.loaderSvc.removeAllRequest(environment.LOADER_ID);
      })
      .catch((error: any) => {
        if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
          return;
        }

        this.psNotSvc.showPurpleSimpleNotification({
          title: this.tsvc.getTranslationValue(this.T.social_login_error_title,[] ),
          message: this.tsvc.getTranslationValue(this.T.social_login_error_message, []),
          buttonText: this.tsvc.getTranslationValue(this.T.social_login_error_button, []),
          autoClose: true
        });
      });
  }

  changeCompany() {
      this.psNotSvc.showPurpleSimpleNotification({
          title: this.tsvc.getTranslationValue(this.T.change_company_modal_title, []),
          message: this.tsvc.getTranslationValue(this.T.change_company_modal_message, []),
          buttonText: this.tsvc.getTranslationValue(this.T.change_company_modal_button, []),
          buttonFunction: () => {
            this.storageSvc.clear();
        this.location.historyGo(1);
        const url = `${this.tsvc.currentLanguage()}`;
        this.location.go(url);
        window.location.reload();
          },
          cancelBottonText: this.tsvc.getTranslationValue(this.T.change_company_modal_cancel_button, []),
          autoClose: true
        });

  }
}

